import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import makeStyles from '@mui/styles/makeStyles'
import { useTheme } from '@mui/styles'
import EditIcon from '@mui/icons-material/Edit'

import { useHistory, useParams } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'
import { useEnhancerPlatforms } from '../../contexts/EnhancerContext'

import { API_ROOT_URL } from '../../constants'
import * as ROUTES from '../../constants/routes'

import vidalyticsLogoImg from '../../media/vidalytics_logo.png'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be enhanced from this account. Redirecting back to settings...'

// Hints for which cart field typically carries the Vidalytics session_id, shown per config card.
const SESSION_FIELD_HINTS = {
  sticky: 'The token key of the Sticky custom field that holds the Vidalytics session id (e.g. vidalytics_session_id)',
}

const VidalyticsEnhancer = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)
  const { fetchEnhancerPlatforms } = useEnhancerPlatforms()

  const [showCopyTooltip, setShowCopyTooltip] = React.useState(false)

  const [message, setMessage] = React.useState('')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity, setMessageSeverity] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)
  const [initialIntegrationIds, setInitialIntegrationIds] = React.useState([])
  const [selectedIntegrationIds, setSelectedIntegrationIds] = React.useState([])
  const [editingApiKey, setEditingApiKey] = React.useState(false)
  const [newApiKey, setNewApiKey] = React.useState('')
  const [editingNickname, setEditingNickname] = React.useState(false)
  const [newNickname, setNewNickname] = React.useState('')
  const [configMap, setConfigMap] = React.useState({})
  const [initialConfigMap, setInitialConfigMap] = React.useState({})

  const {
    isLoading: isLoadingEnhancer,
    data: enhancer = {},
    refetch: refetchEnhancer
  } = useQuery(['enhancer-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          let cfg = data.config || {}
          if (typeof cfg === 'string') {
            try { cfg = JSON.parse(cfg) } catch (error) { cfg = {} }
          }
          setConfigMap(cfg)
          setInitialConfigMap(cfg)
        }
      }
    }
  )

  const {
    isLoading: isLoadingEnhancerIntegrationIds,
  } = useQuery(['enhancer-integrations-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/${id}/integrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setInitialIntegrationIds(data)
          setSelectedIntegrationIds(data)
        }
      }
    }
  )

  // Vidalytics enhances Sticky cart integrations (V1). Only supported cart platforms can be selected,
  // because the session_id resolver only knows how to re-query those carts' order APIs.
  const {
    isLoading: isLoadingStickyIntegrations,
    data: stickyIntegrations = []
  } = useQuery(['integrations-list', 'sticky'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/platform/sticky`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(res => res.json())
    }),
    { cacheTime: 0, staleTime: 0, refetchOnWindowFocus: false }
  )

  const integrations = Array.isArray(stickyIntegrations) ? stickyIntegrations : []
  const isLoadingIntegrations = isLoadingStickyIntegrations

  const { isLoading: isLoadingRevoke, refetch: revoke } = useQuery(['revoke-vidalytics'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/vidalytics/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enhancer_id: id })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnReconnect: false,
      onSuccess: () => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        setShowMessage(true)
        fetchEnhancerPlatforms()
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
      },
    }
  )

  const { isLoading: isUpdatingEnhancerIntegrations, mutate: updateEnhancerIntegrations } = useMutation(
    newIntegrationIds => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/integrations`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enhancer_id: id,
          integration_ids: newIntegrationIds,
        }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setInitialIntegrationIds(selectedIntegrationIds)
        setShowMessage(true)
        setMessage('Data Sources successfully updated.')
        setMessageSeverity('success')
      },
      onError: () => {
        setSelectedIntegrationIds(initialIntegrationIds)
        setShowMessage(true)
        setMessage('Error updating Data Sources. Selected sources reverted. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isSavingConfig, mutate: saveConfig } = useMutation(
    newConfig => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/config`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enhancer_id: id, config: newConfig }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setInitialConfigMap(configMap)
        setShowMessage(true)
        setMessage('Session field configuration saved.')
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error saving configuration. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isTestingConnection, mutate: testConnection } = useMutation(
    () => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/vidalytics/test-connection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enhancer_id: id }),
      }).then(res => res.json())
    }),
    {
      onSuccess: (data) => {
        setShowMessage(true)
        if (data && data.connected) {
          setMessage(data.accountName
            ? `Connection successful — linked to ${data.accountName}.`
            : 'Connection successful — your Vidalytics API key is valid.')
          setMessageSeverity('success')
        } else {
          setMessage('Connection failed — check that your Vidalytics API key is correct.')
          setMessageSeverity('error')
        }
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error testing connection. Please try again.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isUpdatingApiKey, mutate: updateApiKey } = useMutation(
    () => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/vidalytics/update-api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enhancer_id: id, apiKey: newApiKey }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setEditingApiKey(false)
        setNewApiKey('')
        refetchEnhancer()
        setShowMessage(true)
        setMessage('Vidalytics API key updated.')
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error updating the Vidalytics API key. Please try again.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isUpdatingNickname, mutate: updateNickname } = useMutation(
    () => firebase.auth().currentUser.getIdToken(false).then(async token => {
      const res = await fetch(`${API_ROOT_URL}/api_fs/enhancers/vidalytics/update-nickname`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enhancer_id: id, nickname: newNickname }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || 'Could not update the name. Please try again.')
      return body
    }),
    {
      onSuccess: () => {
        setEditingNickname(false)
        setNewNickname('')
        refetchEnhancer()
        setShowMessage(true)
        setMessage('Name updated.')
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error updating the name. Please try again.')
        setMessageSeverity('error')
      }
    }
  )

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((value, index) => value === sortedB[index])
  }

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const handleIntegrationChange = (integrationId) => {
    if (selectedIntegrationIds.includes(integrationId)) {
      setSelectedIntegrationIds(selectedIntegrationIds.filter(existingId => existingId !== integrationId))
    } else {
      setSelectedIntegrationIds([...selectedIntegrationIds, integrationId])
    }
  }

  const handleClear = () => setSelectedIntegrationIds([])
  const handleSelectAll = () => setSelectedIntegrationIds(integrations.map(integration => integration.id))
  const handleUpdateEnhancerIntegrations = () => updateEnhancerIntegrations(selectedIntegrationIds)

  const handleSessionFieldChange = (integrationId, value) => {
    setConfigMap({
      ...configMap,
      [integrationId]: { ...(configMap[integrationId] || {}), sessionIdField: value },
    })
  }

  const handleSaveConfig = () => saveConfig(configMap)

  const copyToClipboard = (text, setTooltip) => {
    navigator.clipboard.writeText(text)
    setTooltip(true)
  }

  const maskText = (apiKey) => {
    if (!apiKey) return ''
    const visibleChars = 4
    const maskedSection = apiKey.slice(visibleChars, -visibleChars).replace(/./g, '*')
    return apiKey.slice(0, visibleChars) + maskedSection + apiKey.slice(-visibleChars)
  }

  const dataSourcesChanged = !arraysEqual(initialIntegrationIds, selectedIntegrationIds)
  const configChanged = JSON.stringify(configMap) !== JSON.stringify(initialConfigMap)

  // Config cards are shown for the currently-saved linked integrations.
  const linkedIntegrations = integrations.filter(integration => initialIntegrationIds.includes(integration.id))

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='vidalytics_logo'
                    src={vidalyticsLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    Vidalytics Enhancer
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Vidalytics Account ID (the account guid returned by Vidalytics — the enhancer's platform_id) */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Account ID</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : `${enhancer.platform_id}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Name (auto-populated from the Vidalytics account; editable to a friendly label) */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Name</Typography>
                </Grid>
                <Grid item xs={10}>
                  {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : (
                    editingNickname ? (
                      <Box display='flex' flexDirection='row' width='100%' alignItems='center'>
                        <TextField
                          label='Name'
                          value={newNickname}
                          onChange={(event) => setNewNickname(event.target.value)}
                          variant='outlined'
                          size='small'
                          fullWidth
                          autoFocus
                        />
                        <Button
                          variant='contained'
                          color='primary'
                          sx={{ ml: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => updateNickname()}
                          disabled={!newNickname.trim() || isUpdatingNickname}
                        >
                          {isUpdatingNickname ? <CircularProgress color='inherit' size={20} /> : 'Save'}
                        </Button>
                        <Button
                          variant='text'
                          sx={{ ml: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => { setEditingNickname(false); setNewNickname('') }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box display='flex' flexDirection='row' width='100%' alignItems='center'>
                        <Typography variant='body2'>{`${enhancer.nickname}`}</Typography>
                        <Tooltip title='Edit name' placement='top'>
                          <IconButton
                            size='small'
                            color='primary'
                            sx={{ flexShrink: 0, ml: 1 }}
                            onClick={() => { setNewNickname(enhancer.nickname || ''); setEditingNickname(true) }}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Vidalytics API Key (the key we use to call Vidalytics; update it if Vidalytics regenerates theirs) */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Vidalytics API Key</Typography>
                </Grid>
                <Grid item xs={10}>
                  {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : (
                    editingApiKey ? (
                      <Box display='flex' flexDirection='row' width='100%' alignItems='center'>
                        <TextField
                          label='New Vidalytics API Key'
                          value={newApiKey}
                          onChange={(event) => setNewApiKey(event.target.value)}
                          variant='outlined'
                          size='small'
                          fullWidth
                          autoFocus
                        />
                        <Button
                          variant='contained'
                          color='primary'
                          sx={{ ml: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => updateApiKey()}
                          disabled={!newApiKey || isUpdatingApiKey}
                        >
                          {isUpdatingApiKey ? <CircularProgress color='inherit' size={20} /> : 'Save'}
                        </Button>
                        <Button
                          variant='text'
                          sx={{ ml: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => { setEditingApiKey(false); setNewApiKey('') }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box display='flex' flexDirection='row' width='100%' alignItems='center'>
                        <InputBase
                          value={maskText(enhancer.api_key)}
                          size='small'
                          fullWidth
                          disabled
                        />
                        <Tooltip title='Edit API key' placement='top'>
                          <IconButton
                            size='small'
                            color='primary'
                            sx={{ flexShrink: 0, mr: 1 }}
                            onClick={() => setEditingApiKey(true)}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          open={showCopyTooltip}
                          onClose={() => setShowCopyTooltip(false)}
                          leaveDelay={2000}
                          placement='right'
                          title='Copied'
                        >
                          <Button
                            variant='text'
                            color='primary'
                            sx={{ flexShrink: 0 }}
                            onClick={() => copyToClipboard(enhancer.api_key, setShowCopyTooltip)}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                        <Button
                          variant='outlined'
                          color='primary'
                          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => testConnection()}
                          disabled={isTestingConnection}
                        >
                          {isTestingConnection ? <CircularProgress color='inherit' size={20} /> : 'Test Connection'}
                        </Button>
                      </Box>
                    )
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Data Sources */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Data Sources</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='block'>
                    <Typography variant='body2' paragraph>
                      Select the shopping cart Data Sources that you would like enhanced with Vidalytics
                      video data. Sticky.io carts are supported. A single Vidalytics enhancer can enhance
                      multiple carts.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      For each selected cart, set which field carries the Vidalytics session id in the
                      configuration section below.
                    </Typography>
                  </Box>

                  {/* Integrations List */}
                  <Box
                    marginTop={1}
                    marginBottom={1}
                    border={1}
                    borderColor={theme.palette.divider}
                    borderRadius={1}
                    padding={1}
                    maxHeight={400}
                    overflow='auto'
                  >
                    {isLoadingIntegrations || isLoadingEnhancerIntegrationIds ? (
                      <Skeleton variant='text' width='100%' />
                    ) : (
                      <Box>
                        {integrations.length === 0 ? (
                          <Typography variant='body2' color='textSecondary'>
                            No supported cart Data Sources found (Sticky.io)
                          </Typography>
                        ) : (
                          <List dense>
                            {integrations
                              .sort((a, b) => a.nickname.localeCompare(b.nickname))
                              .map(integration => {
                                const labelId = `checkbox-list-label-${integration.id}`
                                return (
                                  <ListItem
                                    key={integration.id}
                                    dense
                                    disablePadding
                                    sx={{ bgcolor: selectedIntegrationIds.includes(integration.id) ? theme.palette.action.selected : 'inherit' }}
                                  >
                                    <ListItemButton
                                      onClick={() => handleIntegrationChange(integration.id)}
                                      dense
                                      sx={{ height: 40 }}
                                    >
                                      <ListItemIcon>
                                        <Checkbox
                                          edge='start'
                                          checked={selectedIntegrationIds.includes(integration.id)}
                                          tabIndex={-1}
                                          disableRipple
                                          inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                      </ListItemIcon>
                                      <ListItemText id={labelId} primary={`${integration.nickname} (${integration.platform})`} />
                                    </ListItemButton>
                                  </ListItem>
                                )
                              })}
                          </List>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography variant='body2'>
                      {selectedIntegrationIds.length} Data Sources selected for enhancement
                      {dataSourcesChanged && (<span>&nbsp;<b>(unsaved changes)</b></span>)}
                    </Typography>
                  </Box>
                  <Box marginTop={2} display='flex' justifyContent='flex-start' columnGap={1}>
                    <Button variant='outlined' onClick={handleClear}>Clear</Button>
                    <Button variant='outlined' onClick={handleSelectAll}>Select All</Button>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleUpdateEnhancerIntegrations}
                      disabled={!dataSourcesChanged}
                    >
                      {isUpdatingEnhancerIntegrations ? <CircularProgress color='inherit' size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Session ID Field Configuration (one card per linked cart) */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Session Field</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' paragraph>
                    For each linked Sticky cart, enter the <b>token key</b> of the custom field that carries
                    the Vidalytics session id (the value passed through by the Vidalytics landing page script).
                    Sticky returns custom fields keyed by token; this is how we know where to read the session
                    id when enhancing a payment.
                  </Typography>
                  {linkedIntegrations.length === 0 ? (
                    <Typography variant='body2' color='textSecondary'>
                      Link one or more cart Data Sources above, then configure their session fields here.
                    </Typography>
                  ) : (
                    linkedIntegrations.map(integration => (
                      <Box
                        key={integration.id}
                        marginBottom={2}
                        border={1}
                        borderColor={theme.palette.divider}
                        borderRadius={1}
                        padding={2}
                      >
                        <Typography variant='subtitle1' gutterBottom>
                          {integration.nickname} ({integration.platform})
                        </Typography>
                        <TextField
                          sx={{ mt: 1.5 }}
                          label='Sticky Custom Field (token key)'
                          value={(configMap[integration.id] && configMap[integration.id].sessionIdField) || ''}
                          onChange={(event) => handleSessionFieldChange(integration.id, event.target.value)}
                          helperText={SESSION_FIELD_HINTS[integration.platform] || 'The cart order field that holds the Vidalytics session id'}
                          variant='outlined'
                          size='small'
                          fullWidth
                        />
                      </Box>
                    ))
                  )}
                  {linkedIntegrations.length > 0 && (
                    <Box marginTop={1}>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={handleSaveConfig}
                        disabled={!configChanged}
                      >
                        {isSavingConfig ? <CircularProgress color='inherit' size={24} /> : 'Save Configuration'}
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Delete Enhancer */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>Delete</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data enhancer so payments are no longer enhanced with
                    Vidalytics video data, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Enhancer
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to <b>{enhancer.nickname}</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent payments from being enhanced with Vidalytics video data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => revoke()}
          >
            {isLoadingRevoke ? <CircularProgress color='inherit' size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={3000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

VidalyticsEnhancer.propTypes = {}

export default VidalyticsEnhancer
